import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { NAV_ITEMS } from '../data/sectionsData';
import useReveal from '../hooks/useReveal';
import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import AboutSection from '../components/AboutSection';
import SkillsSection from '../components/SkillsSection';
import ProjectsSection from '../components/ProjectsSection';
import BlogSection from '../components/BlogSection';
import YouTubeSection from '../components/YouTubeSection';
import RidesSection from '../components/RidesSection';
import ArtSection from '../components/ArtSection';
import PhotographySection from '../components/PhotographySection';
import ContactSection from '../components/ContactSection';
import Lightbox from '../components/Lightbox';
import '../App.css';

/* ─────── Initial visible counts per section ─────── */
const INITIAL_VISIBLE = {
    projects: 3,
    youtube: 3,
    drawings: 4,
    photos: 8,
};

/* ════════════════════════════════════
   HOME PAGE
   ════════════════════════════════════ */
export default function HomePage() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const [lightboxItem, setLightboxItem] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const containerRef = useReveal([expandedSections]);
    const location = useLocation();

    const toggleSection = useCallback((section) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    }, []);

    const getVisibleItems = useCallback((section, items) => {
        const limit = INITIAL_VISIBLE[section];
        return expandedSections[section] ? items : items.slice(0, limit);
    }, [expandedSections]);

    /* Scroll to hash on load (e.g. from /#blog link) */
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            setTimeout(() => {
                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [location]);

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 40);
            const sections = NAV_ITEMS.map((n) => document.getElementById(n.id));
            for (let i = sections.length - 1; i >= 0; i--) {
                const sec = sections[i];
                if (sec && sec.getBoundingClientRect().top <= 200) {
                    setActiveSection(NAV_ITEMS[i].id);
                    break;
                }
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = (mobileOpen || lightboxItem) ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen, lightboxItem]);

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setMobileOpen(false);
    };

    return (
        <div className="min-h-screen bg-surface font-sans text-on-surface" ref={containerRef}>
            <Navbar
                scrolled={scrolled}
                activeSection={activeSection}
                scrollTo={scrollTo}
                mobileOpen={mobileOpen}
                setMobileOpen={setMobileOpen}
            />
            <HeroSection scrollTo={scrollTo} />
            <AboutSection />
            <SkillsSection />
            <ProjectsSection
                expanded={expandedSections.projects}
                onToggle={() => toggleSection('projects')}
                getVisibleItems={getVisibleItems}
            />
            <BlogSection />
            <YouTubeSection
                expanded={expandedSections.youtube}
                onToggle={() => toggleSection('youtube')}
                getVisibleItems={getVisibleItems}
            />
            <RidesSection />
            <ArtSection
                expanded={expandedSections.drawings}
                onToggle={() => toggleSection('drawings')}
                getVisibleItems={getVisibleItems}
                onOpenLightbox={setLightboxItem}
            />
            <PhotographySection
                expanded={expandedSections.photos}
                onToggle={() => toggleSection('photos')}
                getVisibleItems={getVisibleItems}
                onOpenLightbox={setLightboxItem}
            />
            <ContactSection />
            <Lightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />
        </div>
    );
}
